/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365 from '@msdyn365-commerce/core';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICartViewProps, IOrderSummaryErrors } from '@msdyn365-commerce-modules/cart';
import { ICartlinesViewProps } from '@msdyn365-commerce-modules/cart';
import { ICartLineItemViewProps } from '@msdyn365-commerce-modules/cart';

//----------------------------------------------------------
// @CUSTOM: Extended config imports
//----------------------------------------------------------
import classnames from 'classnames';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: drop ship messaging imports
//----------------------------------------------------------
import { getCatalogId } from '@msdyn365-commerce/core';
import { ICartProps } from '../definition-extensions/cart.ext.props.autogenerated';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy'
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Kit custom imports
//----------------------------------------------------------
import { ICartData } from '@msdyn365-commerce-modules/cart'
import { Button } from '@msdyn365-commerce-modules/utilities';
import { attrNames } from '../../../utilities/global-constants';
import { convertCartAttributes, convertProductAttributes } from '../../../utilities/data-attribute-parser';
import { clsHelper } from '../../../utilities/class-name-helper';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Kit display - get elective cart lines tied to
// kit item
//----------------------------------------------------------
const getElectiveCartLines = (kitCartLine: ICartlinesViewProps, cartLines: ICartlinesViewProps[]) => {
    return cartLines.filter(cartLine => {
        const kitAttributes = kitCartLine.data?.cartline.AttributeValues;
        const parsedKitAttributes = kitAttributes && convertCartAttributes(kitAttributes);
        const attributes = cartLine.data?.cartline.AttributeValues;
        const parsedAttributes = attributes && convertCartAttributes(attributes);
        return parsedKitAttributes && parsedAttributes && parsedKitAttributes[attrNames.kitLineChildAssociation] === parsedAttributes[attrNames.kitLineParentAssociation];
    });
};
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Kit display - remove kit item alongside tied
// elective items
//----------------------------------------------------------
const removeKitItem = async (kitCartLine: ICartlinesViewProps, cartLines: ICartlinesViewProps[], cartData: ICartData) => {
    const electiveCartLines = getElectiveCartLines(kitCartLine, cartLines);

    if (cartData.cart.result) {
        const input = {
            cartLineIds: [kitCartLine.cartlineId!.toString()]
        };

        if (electiveCartLines) {
            electiveCartLines.forEach(electiveCartLine => {
                input.cartLineIds.push(electiveCartLine.cartlineId!.toString());
            });
        }

        cartData.cart.result.removeCartLines(input);
    }
};
//----------------------------------------------------------

const _renderCartlines = (
    cartLines: ICartlinesViewProps[] | undefined,
    cartEmptyText: string,
    CartlinesWrapper: INodeProps,
    storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    cartDataResult: boolean,
    cartData: ICartData,    // @CUSTOM: Needed for kit display
    // @CUSTOM: drop ship messaging imports
    productAttr: ProductSearchResult[],
    dropShipMessage: string
): (JSX.Element | null)[] | JSX.Element => {    // @CUSTOM: Kit display - updated return type to account for no cart lines being returned
    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    if (cartLines) {
        return cartLines.map(cartLine => {

            // @CUSTOM: drop ship messaging imports
            const attributes = productAttr?.find(item => {
                return item.ItemId === cartLine.data?.cartline.ItemId
            })?.AttributeValues || [];

            const formatAttr = convertProductAttributes(attributes);

            const isDropShip = formatAttr[attrNames.dropShip];

            //----------------------------------------------
            // @CUSTOM: Kit display
            //----------------------------------------------
            const cartLineData = cartLine.data?.cartline;
            const lineAttributes = cartLineData?.AttributeValues && convertCartAttributes(cartLineData.AttributeValues);
            const kitElectives = lineAttributes && lineAttributes[attrNames.kitElectives];

            // Do not return kit elective cart lines
            if (lineAttributes && lineAttributes[attrNames.kitLineParentAssociation]) {
                return null;
            }

            const removeProps = React.isValidElement(cartLine.remove) && cartLine.remove.props;
            //----------------------------------------------

            return (
                <div className='msc-cart-lines-item' key={`${cartLine.cartlineId ?? ''}-${MsDyn365.isBrowser.toString()}`}>
                    {cartLine.cartline}
                    {/* @CUSTOM: drop ship messaging imports */}
                    <div className='msc-cart-line__info-messages'>
                        {isDropShip && <div className='msc-cart-line__drop-ship'>{dropShipMessage}</div>}
                    </div>
                    {storeSelector && cartLine.pickUpInStore ? (
                        <Node {...cartLine.pickUpInStore.ContainerProps}>{cartLine.pickUpInStore.defaultComponent}</Node>
                    ) : null}
                    {/* @CUSTOM: Restructured the node below for ease of styling */}
                    <Node className='msc-cart-line__extra-actions'>
                        {/* @CUSTOM: Replaced Remove button below with custom remove cartline handler if item is a kit with electives */}
                        {kitElectives
                            ? <Button {...removeProps} onClick={() => removeKitItem(cartLine, cartLines, cartData)} />
                            : cartLine.remove
                        }
                        {cartLine.addToWishlist}
                        {cartLine.addToOrderTemplate}
                    </Node>
                    {/* @CUSTOM: End restructuring */}
                </div>
            );
        });
    }
    return cartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <p className='msc-cart-line'>{cartEmptyText}</p>
            {backToShoppingButton}
        </div>
    ) : (
        <>{waitingComponent}</>
    );
};

/**
 * Method will render error block.
 * @param errorData -The order summary errors interface.
 * @returns Jsx element.
 */
const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || !ArrayExtensions.hasElements(errorData.errors)) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

/**
 * Method will render invoice summary lines.
 * @param invoiceSummaryLines -The invoicesummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Method will render order summary lines.
 * @param orderSummaryLines -The ordersummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!orderSummaryLines) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {/* @CUSTOM: moved summary lines inside container div */}
            <div className='msc-order-summary__summary'>
                {/* @CUSTOM: move promo code to below order total */}
                {orderSummaryLines.subtotal}
                {orderSummaryLines.shipping}
                {orderSummaryLines.otherCharge}
                {orderSummaryLines.tax}
                {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
                {orderSummaryLines.orderTotal}
            </div>
            {/* @CUSTOM: moved promo inside a container div */}
            <div className='msc-promo-code'>
                {props.promoCode}
            </div>
        </Node>
    );
};

/**
 * Method will render cart promotions.
 * @param promotions -The cartline item view props.
 * @returns Jsx element.
 */
const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

/**
 * Method will render cartlines group title.
 * @param cartLine -The cartline view props.
 * @param titleSeparate -The title separate.
 * @returns Jsx element.
 */
const renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, titleSeparate: string) => {
    const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
    return (
        <>
            <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>
                        {cartLine.pickUpInStore?.deliveryOption}
                        {titleSeparate}
                        {cartLine.pickUpInStore?.orgUnitName}
                    </p>
                ) : null}
            </Node>
        </>
    );
};

/**
 * Method will render cartlines groups header title.
 * @param pickUpText -The pickup text for group.
 * @param shippingText -The shipping text for group.
 * @param emailShippingText -The email shipping text for group.
 * @param titleSeparate -The title separator.
 * @param cartLine -The cartline view props.
 * @param emailDeliveryModeCode -The email delivery code from channel.
 * @returns Jsx element.
 */
const renderCartLinesGroupHeader = (
    pickUpText: string,
    shippingText: string,
    emailShippingText: string,
    titleSeparate: string,
    cartLine: ICartlinesViewProps,
    emailDeliveryModeCode?: string
) => {
    const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    if (!StringExtensions.isNullOrEmpty(emailDeliveryModeCode)) {
        groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailShippingText : groupTitle;
    }

    return (
        <>
            <div className='msc-cart-lines-group-wraper__bopis-heading'>
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}-icon`} />
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}`}>{groupTitle}</p>
                {renderCartLinesGroupTitle(cartLine, titleSeparate)}
            </div>
        </>
    );
};

/**
 * Method will render cartlines in groups.
 * @param props -The cartline.
 * @returns Jsx element.
 */
const renderCartLinesGroup = (props: ICartViewProps & ICartProps<{}>, productAttr: ProductSearchResult[]): JSX.Element | undefined => {  // @CUSTOM: drop ship messaging imports
    if (props.cartLinesGroup && ArrayExtensions.hasElements(props.cartLinesGroup)) {
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup.map(cartlines => {
                    return (
                        <div key={cartlines[0].cartlineId} className='msc-cart-lines-group-wraper'>
                            {renderCartLinesGroupHeader(
                                props.resources.pickUpText,
                                props.resources.shippingText,
                                props.resources.emailshippingText,
                                props.resources.titleSeparate,
                                cartlines[0],
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                            )}
                            {_renderCartlines(
                                cartlines,
                                props.resources.emptyCartText,
                                props.CartlinesWrapper,
                                props.storeSelector,
                                props.backToShoppingButton,
                                props.waitingComponent,
                                props.cartLoadingStatus,
                                props.cartDataResult,
                                props.data,      // @CUSTOM: Kit display - need access to cart data to connect parent kit with elective cart lines
                                productAttr,     // @CUSTOM: drop ship messaging imports
                                props.resources.dropShipMessage     // @CUSTOM: drop ship messaging imports
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
    return props.cartDataResult ? (
        <div className='msc-cartline-wraper'>
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>{props.resources.emptyCartText}</p>
                {/* @CUSTOM: Moved button into container div */}
                <div className='msc-cart__btn-group'>
                    {props.backToShoppingButton}
                </div>
            </div>
        </div>
    ) : (
        <>{props.waitingComponent}</>
    );
};

//----------------------------------------------------------
// @CUSTOM: Render slot items
//----------------------------------------------------------
const _renderSlotItems = (items: React.ReactNode[]): JSX.Element => {
    return (
        <>
            {items.map((slot: React.ReactNode, index: number) => (
                <React.Fragment key={index}>
                    {slot}
                </React.Fragment>
            ))}
        </>
    );
}

//----------------------------------------------------------
// @CUSTOM: Render empty cart button
//----------------------------------------------------------
const renderRemoveFromCart = (props: ICartProps<ICartData>): JSX.Element | null => {
    if (!props.data.cart.result?.cart.CartLines?.length) {
        return null;
    }

    return (
        <button className='empty-cart__button' onClick={() => {
            const cartLineIds = props.data.cart.result?.cart.CartLines?.map(line => {
                return line.LineId || '';
            }) || [];
            props.data.cart.result?.removeCartLines({ cartLineIds: cartLineIds });
        }}>
            {props.resources.emptyCartButtonLabel}
        </button>
    );
}

const rsmRenderPrintableCartButton = (): JSX.Element | null => {
    const handleNavigation = () => {
        window.print()
    };
    return (<button className={clsHelper('cart', 'printable-cart-button')}onClick={handleNavigation}>Print Cart</button>)
}

const CartView: React.FC<ICartViewProps & ICartProps<ICartData>> = (props) => {     // @CUSTOM: extended definition props

    // @CUSTOM: drop ship messaging imports
    const [productAttr, productAttrLoaded] = React.useState([] as ProductSearchResult[]);
    React.useEffect(() => {

        const products = props.data.products?.result;

        const productIds = products?.map(product => {
            return { ItemId: product.ItemId };
        });

        const productAttributesInput = {
            ItemIds: productIds,
            IncludeProductsFromDescendantCategories: false,
            SkipVariantExpansion: true,
            IncludeAttributes: true,
            Context: {
                ChannelId: props.context.request.apiSettings.channelId,
                CatalogId: getCatalogId(props.context.request)
            },
        };

        searchByCriteriaAsync({ callerContext: props.context.actionContext }, productAttributesInput).then((attributes) => {
            productAttrLoaded(attributes);
        });

    }, []);
    //------------------------------------------------------

    return (
        // @CUSTOM: Added extended config class name below
        <div className={classnames(props.className, props.config.className)} id={props.id} {...props.renderModuleAttributes(props)}>
            {/* {props.checkoutBlockedDueToUnavailableFunds} */}

            {props.title}
            {!props.cart?.hasInvoiceLine && props.promotionOptions && _renderPromotions(props.promotionOptions)}
            {/* @CUSTOM: Added productAttr below for drop ship */}
            {props.multiplePickUpEnabled ? (
                renderCartLinesGroup(props, productAttr)
            ) : (
                <Node {...props.CartlinesWrapper}>
                    {_renderCartlines(
                        props.cartlines,
                        props.resources.emptyCartText,
                        props.CartlinesWrapper,
                        props.storeSelector,
                        props.backToShoppingButton,
                        props.waitingComponent,
                        props.cartLoadingStatus,
                        props.cartDataResult,
                        props.data,    // @CUSTOM: Kit display - need access to cart data to connect parent kit with elective cart lines
                        productAttr,   // @CUSTOM: drop ship messaging imports
                        props.resources.dropShipMessage     // @CUSTOM: drop ship messaging imports
                    )}
                </Node>
            )}
            <div>
                {props.orderSummaryHeading && rsmRenderPrintableCartButton()}
                {props.orderSummaryHeading && (
                    <Node {...props.OrderSummaryWrapper}>
                        {props.orderSummaryHeading}
                        {props.cart?.hasInvoiceLine
                            ? _renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                            : _renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                        {_renderErrorBlock(props.OrderSummaryErrors)}
                        {/* @CUSTOM: Installment calculator */}
                        {props.slots?.installmentWidget?.length && _renderSlotItems(props.slots.installmentWidget)}
                        {/* @CUSTOM: Moved buttons into container div */}
                        <div className='msc-cart__btn-group'>
                            {props.checkoutAsSignInUserButton}
                            {/* @CUSTOM: Removed checkout as guest {props.checkoutAsGuestButton}*/}
                            {props.expressCheckoutButton && ArrayExtensions.hasElements(props.expressCheckoutButton) ? (
                                <Node {...props.ExpressCheckoutSectionWrapper}>{props.expressCheckoutButton}</Node>
                            ) : null}
                            {props.backToShoppingButton}
                            {/*@CUSTOM: Added the empty cart button*/}
                            {renderRemoveFromCart(props)}
                            {props.createTemplateFromCartButton}
                        </div>
                    </Node>
                )}
            </div>
            {props.storeSelector}
        </div>
    );
};

export default CartView;
